import React, { Component, Fragment } from "react";
import { Card, CardBody, Form, Row, Col, FormGroup, Label } from "reactstrap";
import { Toast } from "primereact/toast";
import { merchantConfigrationsApiService } from "../../services/api/merchants/MerchantConfigrations";
import { GetMerchantConfigrationResponse, MerchantBoricaConfigurationRequest } from "../../models/MerchantModel";
import { Formik, FormikProps, ErrorMessage } from "formik";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import * as Yup from "yup";
import { utils } from "../../utils/utils";
import Loader from "../../components/Loader";
import { isSuperAdmin } from "../../authGuard";

const AUTOMATIC_FINALIZE_KYC_KEY = "AutomaticFinalizeKyc";
const DEFAULT_AUTOMATIC_FINALIZE_KYC_VALUE = "False";

interface GeneralConfigurationProps {
    merchantId: number;
}

interface GeneralConfigurationState {
    isLoading: boolean;
    merchantConfigrations: GetMerchantConfigrationResponse[];
    initialValues: { [key: string]: string };
    validationSchema: Yup.ObjectSchema<any> | undefined;
}

export default class GeneralConfiguration extends Component<GeneralConfigurationProps, GeneralConfigurationState> {
    toast: Toast | null = null;
    formikRef = React.createRef<FormikProps<{ [key: string]: string }>>();

    constructor(props: GeneralConfigurationProps | Readonly<GeneralConfigurationProps>) {
        super(props);

        this.state = {
            isLoading: true,
            merchantConfigrations: [],
            initialValues: {},
            validationSchema: undefined,
        };
    }

    async componentDidMount() {
        await this.loadGeneralConfiguration();
    }

    loadGeneralConfiguration = async () => {
        if (this.props.merchantId) {
            await this.loadGeneralConfigurationData();
        }
        this.setState({ isLoading: false });
    };

    loadGeneralConfigurationData = async () => {
        const configResult = await merchantConfigrationsApiService.getMerchantConfigration(this.props.merchantId);
        if (!configResult.success) {
            this.toast?.show({ severity: "error", summary: "Error", detail: configResult.message, life: 3000 });
            this.setState({ isLoading: false });
            return;
        }

        // Filter general configurations (configurations that are not Borica-specific)
        // You may need to adjust this filtering logic based on your configuration types
        const generalConfigs = configResult.data.filter(
            (config) => config.configrationType === "General" || !config.configrationType
        );

        const formData: { [key: string]: string } = {};
        generalConfigs.forEach((config) => {
            formData[config.key] = config.value ?? "";
        });

        if (!(AUTOMATIC_FINALIZE_KYC_KEY in formData)) {
            formData[AUTOMATIC_FINALIZE_KYC_KEY] = DEFAULT_AUTOMATIC_FINALIZE_KYC_VALUE;
        }

        const validationSchema = this.createValidationSchema(Object.keys(formData));
        this.setState({ merchantConfigrations: generalConfigs, initialValues: formData, validationSchema });
    };

    // Check if a field key contains "Iban" (case-insensitive)
    isIbanField = (key: string): boolean => {
        return key.toLowerCase().includes("iban");
    };

    private static readonly dropdownFields: Record<string, { label: string; value: string }[]> = {
        FastPayProfile: [
            { label: "FastPay", value: "FastPay" },
            { label: "10nPay", value: "10npay" },
        ],
    };

    isDropdownField = (key: string): boolean => {
        return key in GeneralConfiguration.dropdownFields;
    };

    getDropdownOptions = (key: string): { label: string; value: string }[] => {
        return GeneralConfiguration.dropdownFields[key] ?? [];
    };

    // Create validation schema dynamically based on available fields
    createValidationSchema = (fields: string[]): Yup.ObjectSchema<any> => {
        const schema: { [key: string]: Yup.StringSchema } = {};

        fields.forEach((key) => {
            if (this.isIbanField(key)) {
                schema[key] = Yup.string()
                    .test("is-valid-iban", "Invalid IBAN format", function (value) {
                        if (!value || value.trim() === "") {
                            return true; // Allow empty values
                        }
                        return utils.validateIban(value);
                    });
            } else {
                // For non-IBAN fields, you can add other validations as needed
                schema[key] = Yup.string();
            }
        });

        return Yup.object().shape(schema);
    };


    handleSubmit = async (values: { [key: string]: string }) => {
        this.setState({ isLoading: true });

        const configurations: { [key: string]: string } = {};
        Object.entries(values).forEach(([key, value]) => {
            if (value && value.trim() !== "") {
                configurations[key] = value;
            }
        });

        const request: MerchantBoricaConfigurationRequest = {
            configurations: configurations,
        };

        const result = await merchantConfigrationsApiService.saveGeneralConfigration(this.props.merchantId, request);
        if (!result.success) {
            this.setState({ isLoading: false });
            this.toast?.show({ severity: "error", summary: "Error", detail: result.message, life: 3000 });
            return;
        }

        this.setState({ isLoading: true });
        await this.loadGeneralConfigurationData();
        this.setState({ isLoading: false });
        this.toast?.show({ severity: "success", summary: "Success", detail: "General configurations updated successfully.", life: 3000 });
    };

    render() {
        if (this.state.isLoading && Object.keys(this.state.initialValues).length === 0) {
            return (
                <Card>
                    <CardBody>
                        <Loader />
                    </CardBody>
                </Card>
            );
        }

        return (
            <Fragment>
                <Card>
                    <Toast ref={(el) => (this.toast = el)} position={"bottom-center"} baseZIndex={99999}></Toast>
                    {this.state.isLoading ? (
                        <Loader />
                    ) : (
                        <CardBody>
                            <Formik
                                initialValues={this.state.initialValues}
                                enableReinitialize={true}
                                validateOnChange={true}
                                validateOnBlur={true}
                                validationSchema={this.state.validationSchema}
                                onSubmit={async (values, { setSubmitting }) => {
                                    await this.handleSubmit(values);
                                    setSubmitting(false);
                                }}
                            >
                                {({ values, setFieldValue, errors, touched, handleSubmit, validateField }) => (
                                    <Form>
                                        {Object.keys(this.state.initialValues).length === 0 ? (
                                            <div className="p-4 text-center">
                                                <p>No general configuration settings available.</p>
                                            </div>
                                        ) : (
                                            <Row>
                                                {Object.keys(this.state.initialValues).map((key) => {
                                                    const isIban = this.isIbanField(key);
                                                    const hasError = errors[key] && touched[key];
                                                    
                                                    if (key === "FastPayProfile" && !isSuperAdmin()) return null;

                                                    return (
                                                        <Col md={6} key={key}>
                                                            <FormGroup>
                                                                <Label for={key}>
                                                                    {key
                                                                        .replace(/([A-Z])/g, " $1")
                                                                        .replace(/^./, (str) => str.toUpperCase())
                                                                        .trim()}
                                                                    {isIban && <span className="text-danger"> *</span>}
                                                                </Label>
                                                                {this.isDropdownField(key) ? (
                                                                    <Dropdown
                                                                        id={key}
                                                                        value={values[key] || null}
                                                                        options={this.getDropdownOptions(key)}
                                                                        onChange={(e) => setFieldValue(key, e.value)}
                                                                        placeholder="Select..."
                                                                        style={{ height: "45px" }}
                                                                        className={`w-100 form-control ${hasError ? "p-invalid" : ""}`}
                                                                        disabled={this.state.isLoading}
                                                                    />
                                                                ) : (
                                                                    <InputText
                                                                        style={{ height: "45px" }}
                                                                        value={values[key] || ""}
                                                                        onChange={(e) => {
                                                                            setFieldValue(key, e.target.value);
                                                                        }}
                                                                        onBlur={() => {
                                                                            // Validate the field on blur
                                                                            validateField(key);
                                                                        }}
                                                                        name={key}
                                                                        className={`form-control ${hasError ? "is-invalid" : ""}`}
                                                                        disabled={this.state.isLoading}
                                                                        placeholder={isIban ? "Enter IBAN (e.g., GB82WEST12345698765432)" : ""}
                                                                    />
                                                                )}
                                                                <ErrorMessage
                                                                    name={key}
                                                                    component="div"
                                                                    className="invalid-feedback"
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        )}
                                        {Object.keys(this.state.initialValues).length > 0 && (
                                            <Row className="justify-content-center">
                                                <Col xs="auto">
                                                    <FormGroup className="text-center">
                                                        <br />
                                                        <Button
                                                            onClick={() => handleSubmit()}
                                                            label="Submit"
                                                            type="button"
                                                            value="Submit"
                                                            loading={this.state.isLoading}
                                                            disabled={this.state.isLoading}
                                                            className="p-button-text p-button-rounded p-button-plain"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        )}
                                    </Form>
                                )}
                            </Formik>
                        </CardBody>
                    )}
                </Card>
            </Fragment>
        );
    }
}

